import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import StarIcon from '@mui/icons-material/Star';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import './ServiceContainer.css';

const ACCENT = '#ef6a1d';
const NAVY = '#0b2447';

const tierConfig = {
  platinum: {
    color: ACCENT,
    colorRgb: '239,106,29',
    badge: 'PLATINUM',
    badgeIcon: WorkspacePremiumIcon,
  },
  classic: {
    color: '#3b82f6',
    colorRgb: '59,130,246',
    badge: 'CLASSIC',
    badgeIcon: StarIcon,
  },
  flex: {
    color: '#a855f7',
    colorRgb: '168,85,247',
    badge: 'FLEXIBLE',
    badgeIcon: AccessTimeIcon,
  },
};

const detectTier = (title = '') => {
  const t = title.toLowerCase();
  if (t.includes('platinum')) return 'platinum';
  if (t.includes('classic')) return 'classic';
  return 'flex';
};

const detectIcon = (title = '') => {
  const t = title.toLowerCase();
  if (t.includes('btst') || t.includes('short') || t.includes('long'))
    return AccessTimeIcon;
  if (t.includes('option')) return ShowChartIcon;
  if (t.includes('index') && t.includes('future')) return TrendingUpIcon;
  if (t.includes('future')) return CandlestickChartIcon;
  if (t.includes('index')) return TrendingUpIcon;
  return AutoGraphIcon;
};

const detectTagline = (title = '') => {
  const t = title.toLowerCase();
  if (t.includes('btst') || t.includes('short') || t.includes('long'))
    return 'Pick your horizon — short, medium or long-term picks.';
  if (t.includes('platinum'))
    return 'Premium-tier picks with priority research & support.';
  if (t.includes('classic'))
    return 'Trusted, research-led recommendations for steady growth.';
  return 'Curated for disciplined, focused traders.';
};

const detectFeatures = (title = '') => {
  const t = title.toLowerCase();
  if (t.includes('option') || t.includes('future'))
    return ['Entry / target / SL', 'Risk-managed sizing', 'Live trade alerts'];
  if (t.includes('btst') || t.includes('short') || t.includes('long'))
    return ['Multi-horizon picks', 'Fundamental + technical', 'Position guidance'];
  return ['Daily research', 'Verified entries', 'Live support'];
};

const ServiceContainer = ({
  title,
  route,
  icon,
  tagline,
  features,
  index = 0,
}) => {
  const navigate = useNavigate();
  const tier = detectTier(title);
  const cfg = tierConfig[tier];
  const Icon = icon || detectIcon(title);
  const BadgeIcon = cfg.badgeIcon;
  const taglineText = tagline || detectTagline(title);
  const featureList = features || detectFeatures(title);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      style={{ display: 'flex', width: '100%' }}
    >
      <Box
        onClick={() => navigate(route)}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          width: '100%',
          minHeight: 340,
          borderRadius: 4,
          overflow: 'hidden',
          p: 3,
          background: '#fff',
          border: '1px solid rgba(11,36,71,0.06)',
          boxShadow: '0 20px 50px -20px rgba(11,36,71,0.18)',
          transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
          display: 'flex',
          flexDirection: 'column',
          /* Top color edge */
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: `linear-gradient(90deg, ${cfg.color} 0%, rgba(${cfg.colorRgb}, 0.4) 100%)`,
          },
          /* Corner glow */
          '&::after': {
            content: '""',
            position: 'absolute',
            top: -60,
            right: -60,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: `radial-gradient(circle, rgba(${cfg.colorRgb}, 0.18) 0%, rgba(${cfg.colorRgb}, 0) 70%)`,
            pointerEvents: 'none',
            transition: 'opacity 0.4s ease',
          },
          '&:hover': {
            transform: 'translateY(-8px)',
            borderColor: `rgba(${cfg.colorRgb}, 0.35)`,
            boxShadow: `0 30px 60px -20px rgba(${cfg.colorRgb}, 0.3), 0 20px 40px -20px rgba(11,36,71,0.18)`,
          },
          '&:hover .sc-icon': {
            transform: 'scale(1.08) rotate(-6deg)',
            boxShadow: `0 18px 36px -8px rgba(${cfg.colorRgb}, 0.5)`,
          },
          '&:hover .sc-arrow': {
            background: cfg.color,
            color: '#fff',
            borderColor: cfg.color,
            transform: 'rotate(0deg) scale(1.05)',
          },
        }}
      >
        {/* Top row: icon + tier badge */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2.5,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            className="sc-icon"
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${cfg.color} 0%, rgba(${cfg.colorRgb}, 0.6) 100%)`,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: `0 12px 28px -8px rgba(${cfg.colorRgb}, 0.4)`,
              transition: 'all 0.4s cubic-bezier(0.22, 1, 0.36, 1)',
              '& svg': { fontSize: '1.65rem' },
            }}
          >
            <Icon />
          </Box>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.2,
              py: 0.4,
              borderRadius: 5,
              background: `rgba(${cfg.colorRgb}, 0.12)`,
              border: `1px solid rgba(${cfg.colorRgb}, 0.3)`,
              color: cfg.color,
              fontSize: '0.7rem',
              fontWeight: 700,
              letterSpacing: 0.8,
            }}
          >
            <BadgeIcon sx={{ fontSize: '0.85rem' }} />
            {cfg.badge}
          </Box>
        </Box>

        {/* Title */}
        <Box
          component="h3"
          sx={{
            m: 0,
            mb: 1,
            fontSize: '1.3rem',
            fontWeight: 800,
            color: NAVY,
            lineHeight: 1.2,
            letterSpacing: -0.3,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {title}
        </Box>

        {/* Tagline */}
        <Box
          sx={{
            fontSize: '0.9rem',
            color: '#475569',
            lineHeight: 1.6,
            mb: 2.5,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {taglineText}
        </Box>

        {/* Features */}
        <Box sx={{ mb: 2, flex: 1, position: 'relative', zIndex: 1 }}>
          {featureList.map((f) => (
            <Box
              key={f}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                fontSize: '0.85rem',
                color: '#334155',
                py: 0.4,
              }}
            >
              <CheckCircleIcon
                sx={{ fontSize: '0.95rem', color: cfg.color }}
              />
              {f}
            </Box>
          ))}
        </Box>

        {/* CTA row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pt: 2,
            borderTop: '1px solid rgba(11,36,71,0.08)',
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              fontSize: '0.92rem',
              fontWeight: 700,
              color: NAVY,
            }}
          >
            Explore Plan
          </Box>
          <Box
            className="sc-arrow"
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              background: '#fff',
              border: `1.5px solid rgba(${cfg.colorRgb}, 0.35)`,
              color: cfg.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transform: 'rotate(-20deg)',
              transition: 'all 0.35s cubic-bezier(0.22, 1, 0.36, 1)',
              '& svg': { fontSize: '1.15rem' },
            }}
          >
            <ArrowOutwardIcon />
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};

export default ServiceContainer;
